import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  Eye,
  EyeOff,
  Sparkles,
  Eye as EyeIcon,
  BookOpen,
  Newspaper,
  ArrowRight,
  Lock,
  Smartphone,
  Gift,
  Quote,
  Info,
  Check,
  Circle,
} from "lucide-react";
import PublicLayout from "@/components/PublicLayout";
import AgeEligibilityCheck from "@/components/AgeEligibilityCheck";
import { Link } from "react-router-dom";
import statsTiles from "@/assets/preview/stats-tiles.png.asset.json";
import { MIN_PASSWORD_LENGTH, mapPasswordError, passwordTooShortMessage } from "@/lib/passwordPolicy";
import StatsPreviewShowcase, { SHOWCASE_IDS } from "@/components/StatsPreviewShowcase";
import { getContent } from "@/content";
import { isStandaloneDisplay } from "@/lib/standalone";
import { vpSnap } from "@/lib/viewportDiagnostics";

import {
  beginGoogleOAuth,
  clearPendingGoogleOAuth,
} from "@/lib/oauthReturn";



interface AuthPageProps {
  /**
   * Installed Play Store / TWA onboarding presentation: registration-first,
   * onboarding hero copy and the shared showcase rendered as a carousel.
   */
  onboarding?: boolean;
}

export default function AuthPage({ onboarding = false }: AuthPageProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Validate `next` as a same-origin relative path before use.
  const rawNext = searchParams.get("next") ?? "";
  const nextPath = rawNext.startsWith("/") && !rawNext.startsWith("//") ? rawNext : "";
  const postAuthTarget = nextPath || "/app/dashboard";
  // 16+ product eligibility, required on every account-creation path.
  const [ageOk, setAgeOk] = useState(false);
  const tabParam = searchParams.get("tab");
  const defaultTab = tabParam === "signup" ? "signup" : tabParam === "signin" ? "signin" : onboarding ? "signup" : "signin";
  // Brand-new accounts land on the first step of New Match instead of an empty
  // dashboard. Existing users keep their normal destination / `next` route.
  const newUserTarget = "/app/add";
  const authRedirectHandled = useRef(false);

  useEffect(() => {
    // If we returned from Google (or otherwise landed on /auth) with an existing
    // session, forward to the post-auth target immediately.
    const isBrandNew = (session: any) => {
      const created = session?.user?.created_at ? Date.parse(session.user.created_at) : NaN;
      return Number.isFinite(created) && Date.now() - created < 120_000;
    };
    const targetFor = (session: any) =>
      !nextPath && isStandaloneDisplay() && isBrandNew(session) ? newUserTarget : postAuthTarget;
    const handleSession = (session: any) => {
      if (!session || authRedirectHandled.current) return;
      authRedirectHandled.current = true;
      // Google OAuth return recovery (clean full-document load) is handled
      // globally in AuthProvider, because the hosted broker can land on any
      // app route without our callback query string.
      navigate(targetFor(session), { replace: true });
    };
    supabase.auth.getSession().then(({ data }) => {
      vpSnap("supabase-session-restored", { hasSession: !!data.session, source: "getSession" });
      handleSession(data.session);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      vpSnap("supabase-auth-event", { event, hasSession: !!session });
      handleSession(session);
    });

    return () => sub.subscription.unsubscribe();
  }, [navigate, postAuthTarget, nextPath]);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [showSiPwd, setShowSiPwd] = useState(false);
  const [showSuPwd, setShowSuPwd] = useState(false);
  const [signupPwdError, setSignupPwdError] = useState<string | null>(null);
  const lengthOk = password.length >= MIN_PASSWORD_LENGTH;

  const PwdToggle = ({ shown, onClick, label }: { shown: boolean; onClick: () => void; label: string }) => (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1 rounded"
      tabIndex={-1}
    >
      {shown ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
    </button>
  );

  const forgotPassword = async () => {
    if (!email) {
      return toast.error(t("auth.enterEmailFirst", { defaultValue: "Enter your email above first." }));
    }
    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setLoading(false);
    if (error) return toast.error(error.message);
    toast.success(t("auth.resetEmailSent", {
      defaultValue: "If that email exists, we've sent a reset link. Check your inbox.",
    }));
  };

  const signIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (error) {
      // Map credential errors to a safe generic message that also nudges Google users.
      // Do NOT expose raw Supabase error details or reveal whether the email exists.
      const msg = (error.message || "").toLowerCase();
      const isCredentialError =
        msg.includes("invalid login credentials") ||
        msg.includes("invalid credentials") ||
        msg.includes("invalid email or password") ||
        error.status === 400;
      if (isCredentialError) {
        return toast.error(
          t("auth.signInFailed", {
            defaultValue:
              "Email or password is incorrect. If you created your account with Google, sign in with Google.",
          })
        );
      }
      return toast.error(error.message);
    }
    toast.success(t("auth.welcomeBack"));
    navigate(postAuthTarget);
  };

  const signUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ageOk) return toast.error(t("auth.age16Required", { defaultValue: "Please confirm you are at least 16 years old." }));
    if (password.length < MIN_PASSWORD_LENGTH) {
      setSignupPwdError(passwordTooShortMessage(t));
      return;
    }
    setSignupPwdError(null);
    setLoading(true);
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}${onboarding && !nextPath ? newUserTarget : postAuthTarget}`,
        data: { display_name: name },
      },
    });
    setLoading(false);
    if (error) {
      // Map structured password-policy errors to localized copy via the
      // shared helper. Never surface raw English backend messages.
      const msg = mapPasswordError(error, t);
      if (msg) {
        setSignupPwdError(msg);
        return toast.error(msg);
      }

      return toast.error(error.message);
    }
    toast.success(t("auth.accountCreated"));
    navigate(onboarding && !nextPath ? newUserTarget : postAuthTarget);
  };

  /** Google as a REGISTRATION path: eligibility must be confirmed first. */
  const signUpGoogle = async () => {
    if (!ageOk) return toast.error(t("auth.age16Required", { defaultValue: "Please confirm you are at least 16 years old." }));
    await signInGoogle();
  };

  const signInGoogle = async () => {
    setLoading(true);
    // For Google, redirect_uri must be a same-origin public URL. Always return
    // to /auth with the intended post-auth target preserved in `next`, so the
    // effect above forwards to the Dashboard (or requested protected route)
    // as soon as the session hydrates.
    const redirectUri = beginGoogleOAuth(postAuthTarget);
    const result = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: redirectUri,
    });
    if (result.error) {
      clearPendingGoogleOAuth();
      setLoading(false);
      toast.error((result.error as Error).message || "Google sign-in failed");
      return;
    }
    if (result.redirected) return;
    // Popup-based OAuth already has a healthy current document; consume the
    // pending external-return marker and keep the existing SPA transition.
    clearPendingGoogleOAuth();
    navigate(postAuthTarget, { replace: true });
  };


  const heroTitle = onboarding
    ? t("onboarding.heading", { defaultValue: "START BUILDING YOUR TENNIS HISTORY" })
    : t("authPage.heroTitle");
  const heroSub = onboarding
    ? t("onboarding.description", {
        defaultValue:
          "Log matches in a few minutes, track your patterns, and let your stats show what to improve next.",
      })
    : t("authPage.heroSubtitle");
  const landingCopy = getContent(i18n.language).landing;

  const trustPoints = [
    { icon: Gift, text: t("authPage.trustFree") },
    { icon: Lock, text: t("authPage.trustData") },
    { icon: Smartphone, text: t("authPage.trustDevice") },
  ];

  const notReadyCards = [
    {
      to: "/demo",
      icon: EyeIcon,
      title: t("authPage.viewDemoTitle"),
      desc: t("authPage.viewDemoDesc"),
    },
    {
      to: "/tennis-statistics-guide",
      icon: BookOpen,
      title: t("authPage.readGuideTitle"),
      desc: t("authPage.readGuideDesc"),
    },
    {
      to: "/blog",
      icon: Newspaper,
      title: t("authPage.browseBlogTitle"),
      desc: t("authPage.browseBlogDesc"),
    },
  ];

  return (
    <PublicLayout contained={false}>
      <div className="container py-8 sm:py-12 space-y-12">
        {/* HERO + FORM */}
        <section className="grid gap-8 lg:gap-12 lg:grid-cols-2 items-start">
          <div className={`space-y-5 lg:pt-8 ${onboarding ? "order-2 lg:order-1" : ""}`}>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/30 bg-primary/5 text-primary text-xs tracking-wider uppercase">
              <Sparkles className="w-3.5 h-3.5" />{" "}
              {onboarding ? "CLUB PLAYERS DESERVE PRO STATS" : "Tennis Log"}
            </div>
            <h1 className="font-display text-3xl sm:text-5xl tracking-wide leading-tight">
              {heroTitle}
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">{heroSub}</p>
            <ul className="space-y-2 pt-2">
              {trustPoints.map((p) => (
                <li key={p.text} className="flex items-center gap-2 text-sm text-foreground/85">
                  <p.icon className="w-4 h-4 text-primary shrink-0" />
                  <span>{p.text}</span>
                </li>
              ))}
            </ul>
            <div className="hidden lg:block pt-4">
              <Card className="overflow-hidden bg-gradient-card border-border/60 shadow-card">
                <img
                  src={statsTiles.url}
                  alt={t("authPage.dashboardAlt")}
                  loading="lazy"
                  className="w-full h-auto"
                />
                <p className="p-4 text-sm text-muted-foreground leading-relaxed">
                  {t("authPage.dashboardCaption")}
                </p>
              </Card>
            </div>
          </div>

          <div className={`w-full max-w-md mx-auto lg:mx-0 lg:ml-auto animate-fade-in ${onboarding ? "order-1 lg:order-2" : ""}`}>
            <Card className="p-6 bg-gradient-card shadow-elegant border-border/60">
              <Tabs defaultValue={defaultTab} className="w-full">
                <TabsList className="grid grid-cols-2 w-full mb-6">
                  <TabsTrigger value="signin">{t("auth.signIn")}</TabsTrigger>
                  <TabsTrigger value="signup">{t("auth.signUp")}</TabsTrigger>
                </TabsList>

                <TabsContent value="signin">
                  <Button onClick={signInGoogle} disabled={loading} variant="outline" className="w-full gap-2 border-border/60">
                    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                      <path fill="#4285F4" d="M22.5 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.08-1.92 3.28-4.74 3.28-8.3z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.15-4.53H2.16v2.84C3.97 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.85 14.12c-.22-.66-.35-1.36-.35-2.12s.13-1.46.35-2.12V7.04H2.16A11 11 0 0 0 1 12c0 1.78.43 3.46 1.16 4.96l3.69-2.84z"/>
                      <path fill="#EA4335" d="M12 5.4c1.62 0 3.07.56 4.21 1.64l3.16-3.16C17.45 2.1 14.96 1 12 1 7.7 1 3.97 3.47 2.16 7.04l3.69 2.84C6.71 7.33 9.14 5.4 12 5.4z"/>
                    </svg>
                    {t("auth.continueGoogle")}
                  </Button>

                  <p className="mt-3 text-xs text-muted-foreground text-center leading-relaxed">
                    {t("auth.sameMethodHint", {
                      defaultValue: "Use the same sign-in method you used when creating your account.",
                    })}
                  </p>



                  <div className="my-5 flex items-center gap-3">
                    <div className="flex-1 h-px bg-border/60" />
                    <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("auth.or")}</span>
                    <div className="flex-1 h-px bg-border/60" />
                  </div>

                  <form onSubmit={signIn} className="space-y-4">
                    <div>
                      <Label htmlFor="si-email">{t("auth.email")}</Label>
                      <Input id="si-email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
                    </div>
                    <div>
                      <Label htmlFor="si-pwd">{t("auth.password")}</Label>
                      <div className="relative">
                        <Input id="si-pwd" type={showSiPwd ? "text" : "password"} autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} className="pr-10" />
                        <PwdToggle shown={showSiPwd} onClick={() => setShowSiPwd(s => !s)} label={t("auth.togglePassword", { defaultValue: "Show/hide password" })} />
                      </div>
                    </div>
                    <Button type="submit" disabled={loading} className="w-full bg-gradient-primary text-primary-foreground hover:opacity-90 shadow-glow">
                      {loading ? "..." : t("auth.signIn")}
                    </Button>
                    <button
                      type="button"
                      onClick={forgotPassword}
                      disabled={loading}
                      className="block w-full text-center text-xs text-muted-foreground hover:text-primary transition-colors mt-1"
                    >
                      {t("auth.forgotPassword", { defaultValue: "Forgot your password?" })}
                    </button>
                  </form>
                </TabsContent>


                <TabsContent value="signup">
                  <div className="mb-4">
                    <AgeEligibilityCheck checked={ageOk} onChange={setAgeOk} id="su-age16" />
                  </div>
                  <Button onClick={signUpGoogle} disabled={loading} variant="outline" className="w-full gap-2 border-border/60">
                    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                      <path fill="#4285F4" d="M22.5 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.08-1.92 3.28-4.74 3.28-8.3z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.15-4.53H2.16v2.84C3.97 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.85 14.12c-.22-.66-.35-1.36-.35-2.12s.13-1.46.35-2.12V7.04H2.16A11 11 0 0 0 1 12c0 1.78.43 3.46 1.16 4.96l3.69-2.84z"/>
                      <path fill="#EA4335" d="M12 5.4c1.62 0 3.07.56 4.21 1.64l3.16-3.16C17.45 2.1 14.96 1 12 1 7.7 1 3.97 3.47 2.16 7.04l3.69 2.84C6.71 7.33 9.14 5.4 12 5.4z"/>
                    </svg>
                    {t("auth.continueGoogle")}
                  </Button>

                  <div className="my-5 flex items-center gap-3">
                    <div className="flex-1 h-px bg-border/60" />
                    <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("auth.or")}</span>
                    <div className="flex-1 h-px bg-border/60" />
                  </div>

                  <form onSubmit={signUp} className="space-y-4">
                    <div>
                      <Label htmlFor="su-name">{t("auth.name")}</Label>
                      <Input id="su-name" autoComplete="name" required value={name} onChange={(e) => setName(e.target.value)} />
                    </div>
                    <div>
                      <Label htmlFor="su-email">{t("auth.email")}</Label>
                      <Input id="su-email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5">
                        <Label htmlFor="su-pwd">{t("auth.password")}</Label>
                        <Popover>
                          <PopoverTrigger
                            type="button"
                            aria-label={t("auth.passwordInfoLabel", { defaultValue: "Password requirements" })}
                            className="inline-flex items-center justify-center h-6 w-6 rounded-full text-muted-foreground hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                          >
                            <Info className="h-4 w-4" aria-hidden="true" />
                          </PopoverTrigger>
                          <PopoverContent
                            side="top"
                            align="start"
                            collisionPadding={12}
                            className="w-[min(18rem,calc(100vw-2rem))] text-sm leading-relaxed"
                          >
                            {t("auth.passwordHelp", {
                              defaultValue:
                                "Use at least 8 characters. Uppercase letters, numbers or symbols are not required, but avoid very common or easy-to-guess passwords. Passwords that have appeared in known data breaches are not accepted.",
                            })}
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div className="relative">
                        <Input
                          id="su-pwd"
                          type={showSuPwd ? "text" : "password"}
                          autoComplete="new-password"
                          required
                          minLength={MIN_PASSWORD_LENGTH}
                          value={password}
                          onChange={(e) => {
                            setPassword(e.target.value);
                            if (signupPwdError) setSignupPwdError(null);
                          }}
                          onInvalid={(e) => {
                            // Suppress the browser's untranslated validation bubble and
                            // show our localized inline message instead.
                            e.preventDefault();
                            setSignupPwdError(
                              t("auth.passwordTooShort", {
                                defaultValue: "Password must be at least 8 characters.",
                              })
                            );
                          }}
                          aria-invalid={signupPwdError ? true : undefined}
                          aria-describedby={signupPwdError ? "su-pwd-error" : "su-pwd-help"}
                          className="pr-10"
                        />
                        <PwdToggle shown={showSuPwd} onClick={() => setShowSuPwd(s => !s)} label={t("auth.togglePassword", { defaultValue: "Show/hide password" })} />
                      </div>
                      <p
                        id="su-pwd-help"
                        className={`mt-1.5 flex items-center gap-1.5 text-xs ${
                          lengthOk ? "text-primary" : "text-muted-foreground"
                        }`}
                      >
                        {lengthOk ? (
                          <Check className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                        ) : (
                          <Circle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                        )}
                        <span>
                          {t("auth.passwordMinLabel", { defaultValue: "At least 8 characters" })}
                          <span className="sr-only">
                            {lengthOk
                              ? ` — ${t("auth.requirementMet", { defaultValue: "requirement met" })}`
                              : ` — ${t("auth.requirementNotMet", { defaultValue: "requirement not met yet" })}`}
                          </span>
                        </span>
                      </p>
                      {signupPwdError && (
                        <p id="su-pwd-error" role="alert" className="text-xs text-destructive mt-1">
                          {signupPwdError}
                        </p>
                      )}
                    </div>
                    <Button type="submit" disabled={loading} className="w-full bg-gradient-primary text-primary-foreground hover:opacity-90 shadow-glow">
                      {loading ? "..." : t("auth.createAccount")}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>

            </Card>

            <p className="text-center text-xs text-muted-foreground mt-4">
              {t("auth.helpHint", { defaultValue: "New here? " })}
              <Link to="/help" className="text-primary hover:underline">
                {t("auth.helpLink", { defaultValue: "See how Tennis Log works" })}
              </Link>
            </p>
          </div>
        </section>

        {onboarding ? (
          <section className="space-y-4">
            <div className="space-y-2">
              <h2 className="font-display text-2xl tracking-wide uppercase">
                {landingCopy.revealTitle}
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed">{landingCopy.revealSubtitle}</p>
            </div>
            <StatsPreviewShowcase mode="carousel" ids={SHOWCASE_IDS} appendAiTeaser headingTag="h3" />
          </section>
        ) : (
          <>
          {/* FOUNDER QUOTE */}
          <section className="max-w-3xl mx-auto">
            <Card className="p-6 sm:p-8 bg-gradient-card border-primary/30 shadow-glow">
              <Quote className="w-8 h-8 text-primary/70 mb-3" />
              <blockquote className="font-display text-lg sm:text-xl leading-snug tracking-wide text-foreground/95">
                {t("authPage.founderQuote")}
              </blockquote>
              <div className="mt-5 flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-primary flex items-center justify-center font-display text-xl text-primary-foreground shadow-glow">
                  TL
                </div>
                <div className="text-sm">
                  <div className="font-semibold">
                    {t("authPage.founderTitle")}
                  </div>
                  <div className="text-muted-foreground">
                    {t("authPage.founderLocation")}
                  </div>
                </div>
              </div>
            </Card>
          </section>


          {/* NOT READY YET */}
          <section className="border-t border-border/40 pt-10 space-y-6">
            <div className="text-center max-w-2xl mx-auto space-y-2">
              <h2 className="font-display text-2xl sm:text-3xl tracking-wide">
                {t("authPage.notReadyTitle")}
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                {t("authPage.notReadySub")}
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              {notReadyCards.map((card) => (
                <Card key={card.to} className="p-5 bg-gradient-card border-border/60 hover:border-primary/40 transition-colors">
                  <div className="w-10 h-10 rounded-lg bg-gradient-primary flex items-center justify-center mb-3 shadow-glow">
                    <card.icon className="w-5 h-5 text-primary-foreground" strokeWidth={2.4} />
                  </div>
                  <h3 className="font-display text-lg mb-1.5 tracking-wide">{card.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4">{card.desc}</p>
                  <Button asChild variant="outline" size="sm">
                    <Link to={card.to}>
                      {t("authPage.open")} <ArrowRight className="w-3.5 h-3.5 ml-1" />
                    </Link>
                  </Button>
                </Card>
              ))}
            </div>
          </section>
          </>
        )}
      </div>
    </PublicLayout>
  );
}
